import React, { useState, useEffect } from 'react';
import offlineStorage from '../utils/offlineStorage';
import api from '../utils/api';

/**
 * OfflineManager Component
 * Allows users to download events for offline use
 */
const OfflineManager = ({ eventId, eventName }) => {
  const [isOfflineAvailable, setIsOfflineAvailable] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [storageInfo, setStorageInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkOfflineStatus();
    getStorageInfo();
  }, [eventId]);

  const checkOfflineStatus = async () => {
    try {
      const isAvailable = await offlineStorage.isEventOffline(eventId);
      setIsOfflineAvailable(isAvailable);
    } catch (err) {
      console.error('Failed to check offline status:', err);
    }
  };

  const getStorageInfo = async () => {
    try {
      const info = await offlineStorage.getStorageInfo();
      setStorageInfo(info);
    } catch (err) {
      console.error('Failed to get storage info:', err);
    }
  };

  const downloadEventForOffline = async () => {
    setIsDownloading(true);
    setError(null);
    setDownloadProgress(0);

    try {
      // 1. Fetch event data
      console.log('📥 Downloading event data...');
      const eventResponse = await api.get(`/events/${eventId}`);
      const event = eventResponse.data;
      setDownloadProgress(10);

      // 2. Save event
      await offlineStorage.saveEvent(event);
      setDownloadProgress(20);

      // 3. Fetch and save parts
      const parts = event.parts || [];
      console.log(`📥 Downloading ${parts.length} parts...`);
      
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        
        // Get playlists for this part
        const partResponse = await api.get(`/parts/${part.id}`);
        const playlists = partResponse.data.playlists || [];
        
        for (const playlist of playlists) {
          await offlineStorage.savePlaylist(playlist);
          
          // Get tracks for playlist
          const tracksResponse = await api.get(`/playlists/${playlist.id}`);
          const tracks = tracksResponse.data.tracks || [];
          
          console.log(`📥 Downloading ${tracks.length} tracks for playlist ${playlist.name}...`);
          
          for (let j = 0; j < tracks.length; j++) {
            const track = tracks[j];
            await offlineStorage.saveTrack(track);
            
            // Download audio file
            if (track.file_path) {
              try {
                const audioUrl = track.file_path.startsWith('http') 
                  ? track.file_path 
                  : `${process.env.REACT_APP_BACKEND_URL || ''}/storage/uploads/${track.file_path}`;
                
                console.log(`🎵 Downloading audio: ${track.title}`);
                const audioResponse = await fetch(audioUrl);
                const audioBlob = await audioResponse.blob();
                
                await offlineStorage.saveAudioFile(track.file_path, audioBlob);
                
                // Also cache in service worker
                if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                  navigator.serviceWorker.controller.postMessage({
                    type: 'CACHE_AUDIO',
                    url: audioUrl
                  });
                }
              } catch (audioError) {
                console.error(`Failed to download audio for ${track.title}:`, audioError);
              }
            }
            
            // Update progress
            const trackProgress = 20 + ((i + 1) / parts.length) * 70;
            setDownloadProgress(Math.round(trackProgress + (j / tracks.length) * (70 / parts.length)));
          }
        }
      }

      setDownloadProgress(100);
      setIsOfflineAvailable(true);
      
      console.log('✅ Event downloaded for offline!');
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 1000);
      
    } catch (err) {
      console.error('Download failed:', err);
      setError(err.message || 'Téléchargement échoué');
      setIsDownloading(false);
    }
  };

  const removeOfflineEvent = async () => {
    try {
      await offlineStorage.deleteEvent(eventId);
      setIsOfflineAvailable(false);
      await getStorageInfo();
      console.log('🗑️ Event removed from offline');
    } catch (err) {
      console.error('Failed to remove offline event:', err);
      setError(err.message);
    }
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            📴 Mode Offline
          </h3>
          <p className="text-gray-600 text-sm mt-1">
            Téléchargez cet événement pour l'utiliser sans connexion Internet
          </p>
        </div>
      </div>

      {storageInfo && (
        <div className="mb-4 text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span>💾 Stockage utilisé</span>
            <span className="font-mono">
              {storageInfo.usageInMB} MB / {storageInfo.quotaInMB} MB ({storageInfo.percentUsed}%)
            </span>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          ⚠️ {error}
        </div>
      )}

      {isDownloading && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>📥 Téléchargement en cours...</span>
            <span className="font-mono">{downloadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-300"
              style={{ width: `${downloadProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex gap-3">
        {!isOfflineAvailable && !isDownloading && (
          <button
            onClick={downloadEventForOffline}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
          >
            📥 Préparer pour Offline
          </button>
        )}

        {isOfflineAvailable && !isDownloading && (
          <>
            <div className="flex-1 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg font-semibold flex items-center justify-center gap-2">
              ✅ Disponible Offline
            </div>
            <button
              onClick={removeOfflineEvent}
              className="px-4 py-3 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 rounded-lg font-semibold transition-all"
            >
              🗑️ Supprimer
            </button>
          </>
        )}
      </div>

      <div className="mt-4 text-xs text-gray-500">
        💡 Astuce : Téléchargez vos événements avant de partir en soirée pour garantir un fonctionnement sans interruption !
      </div>
    </div>
  );
};

export default OfflineManager;
